import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TON_API_BASE, CONTRACTS, getNetworkConfig } from '../config/contracts';
import { getBalances as getTyJoltBalances, getWalletAddress as getTyJoltAddress, loadOrCreateWallet } from '../services/tyjoltWallet';

export type WalletType = 'tonconnect' | 'walletconnect' | null;

export interface WalletBalances {
  ton: bigint;       // nanoTON
  wton: bigint;      // nanoTON equivalent
  marginDeposited: bigint; // in vault
  marginAvailable: bigint; // available (deposited - locked)
  marginLocked: bigint;    // locked in positions
}

const ZERO_BALANCES: WalletBalances = {
  ton: 0n,
  wton: 0n,
  marginDeposited: 0n,
  marginAvailable: 0n,
  marginLocked: 0n,
};

interface WalletState {
  address: string | null;
  walletType: WalletType;
  balances: WalletBalances;
  isConnected: boolean;
  isConnecting: boolean;
  isLoadingBalances: boolean;
  error: string | null;
  lastBalanceRefresh: number;

  // WalletConnect specific
  wcUri: string | null;
  wcSessionTopic: string | null;

  // Actions
  setAddress: (address: string | null, walletType?: WalletType) => void;
  setBalances: (balances: Partial<WalletBalances>) => void;
  setConnecting: (connecting: boolean) => void;
  setLoadingBalances: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setWcUri: (uri: string | null) => void;
  setWcSession: (topic: string | null) => void;
  disconnect: () => void;
  refreshBalances: () => Promise<void>;
}

async function fetchTonBalance(address: string): Promise<bigint> {
  try {
    const res = await fetch(`${TON_API_BASE}/accounts/${address}`);
    if (!res.ok) return 0n;
    const data = await res.json();
    return BigInt(data.balance || 0);
  } catch {
    return 0n;
  }
}

async function fetchVaultBalance(address: string): Promise<{ available: bigint; locked: bigint }> {
  try {
    // Call PerpVault getGetUserBalance via TON API
    // In testnet: use the stack-based getter approach
    const res = await fetch(
      `${TON_API_BASE}/blockchain/accounts/${CONTRACTS.PerpVault}/methods/getUserBalance?args=${address}`,
    );
    if (!res.ok) return { available: 0n, locked: 0n };
    const data = await res.json();
    const stack = data.decoded?.stack || data.stack || [];
    // Stack: [available, locked]
    const available = BigInt(stack[0]?.value || stack[0]?.num || 0);
    const locked = BigInt(stack[1]?.value || stack[1]?.num || 0);
    return { available, locked };
  } catch {
    return { available: 0n, locked: 0n };
  }
}

let balanceRefreshTimer: ReturnType<typeof setInterval> | null = null;

export const useWalletStore = create<WalletState>()(
  persist(
    (set, get) => ({
      address: null,
      walletType: null,
      balances: ZERO_BALANCES,
      isConnected: false,
      isConnecting: false,
      isLoadingBalances: false,
      error: null,
      lastBalanceRefresh: 0,
      wcUri: null,
      wcSessionTopic: null,

      setAddress: (address, walletType = 'tonconnect') =>
        set({
          address,
          walletType: address ? walletType : null,
          isConnected: !!address,
          isConnecting: false,
          error: null,
        }),

      setBalances: (balances) =>
        set((state) => ({
          balances: { ...state.balances, ...balances },
        })),

      setConnecting: (isConnecting) => set({ isConnecting }),

      setLoadingBalances: (isLoadingBalances) => set({ isLoadingBalances }),

      setError: (error) => set({ error, isConnecting: false }),

      setWcUri: (wcUri) => set({ wcUri }),

      setWcSession: (wcSessionTopic) => set({ wcSessionTopic }),

      disconnect: () => {
        if (balanceRefreshTimer) {
          clearInterval(balanceRefreshTimer);
          balanceRefreshTimer = null;
        }
        set({
          address: null,
          walletType: null,
          balances: ZERO_BALANCES,
          isConnected: false,
          isConnecting: false,
          error: null,
          wcUri: null,
          wcSessionTopic: null,
          lastBalanceRefresh: 0,
        });
      },

      refreshBalances: async () => {
        const { address, isLoadingBalances } = get();
        const networkConfig = getNetworkConfig();

        // TyJolt mode: use TyJolt wallet service
        if (networkConfig.chainType === 'tyjolt') {
          const tyjoltAddr = getTyJoltAddress();
          if (!tyjoltAddr || isLoadingBalances) return;

          set({ isLoadingBalances: true });
          try {
            const balances = await getTyJoltBalances(tyjoltAddr);
            set({
              address: tyjoltAddr,
              isConnected: true,
              walletType: 'tonconnect', // compatibility
              balances: {
                ton: BigInt(Math.floor(balances.public)),
                wton: 0n,
                marginDeposited: BigInt(Math.floor(balances.total)),
                marginAvailable: BigInt(Math.floor(balances.public)),
                marginLocked: BigInt(Math.floor(balances.shielded)),
              },
              lastBalanceRefresh: Date.now(),
              isLoadingBalances: false,
            });
          } catch (err) {
            set({ isLoadingBalances: false });
            console.warn('TyJolt balance refresh failed:', err);
          }
          return;
        }

        // TON mode: use TonConnect
        if (!address || isLoadingBalances) return;

        set({ isLoadingBalances: true });
        try {
          const [ton, vault] = await Promise.all([
            fetchTonBalance(address),
            fetchVaultBalance(address),
          ]);

          set({
            balances: {
              ton,
              wton: 0n, // wTON balance fetched separately if needed
              marginDeposited: vault.available + vault.locked,
              marginAvailable: vault.available,
              marginLocked: vault.locked,
            },
            lastBalanceRefresh: Date.now(),
            isLoadingBalances: false,
          });
        } catch (err) {
          set({ isLoadingBalances: false });
          console.warn('Balance refresh failed:', err);
        }
      },
    }),
    {
      name: 'whether-wallet',
      partialize: (state) => ({
        address: state.address,
        walletType: state.walletType,
        wcSessionTopic: state.wcSessionTopic,
      }),
    },
  ),
);

// Start auto-refresh when address changes
useWalletStore.subscribe((state, prevState) => {
  if (state.address && !prevState.address) {
    // Just connected — fetch immediately
    state.refreshBalances();
    // Set up 30s refresh timer
    if (balanceRefreshTimer) clearInterval(balanceRefreshTimer);
    balanceRefreshTimer = setInterval(() => {
      useWalletStore.getState().refreshBalances();
    }, 30_000);
  } else if (!state.address && prevState.address) {
    // Disconnected — clear timer
    if (balanceRefreshTimer) {
      clearInterval(balanceRefreshTimer);
      balanceRefreshTimer = null;
    }
  }
});
